import type { AppLocale } from '../messages';

/**
 * Строки участка «calls». Свой файл у каждого участка, а не общий `messages.ts` на 4300 строк:
 * участки правятся параллельно, и общий файл давал бы конфликт на каждой новой строке.
 * Ключ добавляется во все четыре словаря — `en` задаёт набор, остальные проверяются типом.
 *
 * <p>Внутри участка строки разложены по трём группам — экран звонка, история звонков,
 * уведомления — по той же причине: их правят независимо, и общая куча снова давала бы
 * конфликт на каждой строке.
 */

// --- экран звонка: сворачивание, итоговая карточка, панель кнопок ---
const enScreen = {
  callAlreadyInCall: 'You are already in a call',
  callReturnAction: 'Return',
  callCameraUnavailable: 'Could not turn on the camera.',
  callMinimize: 'Minimize call',
  callReturnBar: 'Return to call',
  callReturnBarHint: 'Opens the call screen',
  callPhaseCalling: 'Calling…',
  callPhaseRinging: 'Ringing…',
  callEndBusy: 'Busy',
  callEndDeclined: 'Call declined',
  callEndNoAnswer: 'No answer',
  callEndFailed: 'Call dropped',
  callEndEnded: 'Call ended',
  callEndCallAgain: 'Call again',
  callEndMessage: 'Message',
  callCameraStop: 'Stop video',
  callCameraStart: 'Start video',
  callSwitchToVideo: 'Video',
  callCameraOffLabel: 'Camera off',
  callMicOffLabel: 'Microphone off',
  callWeakNetwork: 'Weak connection',
  callTileFocusHint: 'Tap to enlarge',
  callTileUnfocusHint: 'Tap to show everyone',
  callSelfViewHint: 'Drag to move. Tap to swap with the other video.',
  callGroupConfirmTitle: 'Call {count, plural, one {# member} other {# members}}?',
  callGroupConfirmBody: 'Everyone in “{title}” will get the call.',
  callGroupConfirmAction: 'Call',
} as const satisfies Record<string, string>;

// --- история звонков: события в переписке и превью в списке чатов ---
const enHistory = {
  callHistOutgoing: 'Outgoing call',
  callHistOutgoingVideo: 'Outgoing video call',
  callHistIncoming: 'Incoming call',
  callHistIncomingVideo: 'Incoming video call',
  callHistGroup: 'Group call',
  callHistGroupVideo: 'Group video call',
  callHistMissed: 'Missed call',
  callHistMissedVideo: 'Missed video call',
  callHistCancelled: 'Cancelled call',
  callHistNoAnswer: 'No answer',
  callHistDeclinedOutgoing: 'Declined',
  callHistDeclinedIncoming: 'Declined call',
  callHistBusy: 'Busy',
  callHistEnded: 'Call ended',
  callHistCallBackHint: 'Tap to call back',
} as const satisfies Record<string, string>;

// --- уведомления о звонке (Android) и входящий экран ---
const enNotify = {
  callNotifyAnswer: 'Answer',
  callNotifyDecline: 'Decline',
  callNotifyCallBack: 'Call back',
  callNotifyMessage: 'Message',
  callNotifyMissedTitle: 'Missed call',
  callNotifyMissedVideoTitle: 'Missed video call',
  callNotifyMissedGroupTitle: 'Missed group call',
  callNotifyMissedFrom: 'from {name}',
  callNotifySilencedBody: 'Ringing silenced. Tap to open the call',
  callAnswerWithoutVideo: 'Answer without video',
  callReplyWithMessage: 'Reply with message',
  callQuickReplyCantTalk: "Can't talk now. I'll call you back later.",
  callQuickReplyFewMinutes: 'I will call you back in a few minutes.',
  callQuickReplyTextMe: 'Please text me.',
  // Запрос доступа к телефонии (ConnectionService) — callKeepInit.native.ts (UX-50).
  callKeepPermissionTitle: 'Permissions',
  callKeepPermissionBody: 'Phone access is needed to receive incoming calls.',
  callKeepPermissionAllow: 'Allow',
} as const satisfies Record<string, string>;

const en = { ...enScreen, ...enHistory, ...enNotify } as const;

export type CallsMessageKey = keyof typeof en;

type Group<T> = Record<keyof T, string>;

const ruScreen: Group<typeof enScreen> = {
  callAlreadyInCall: 'Вы уже в звонке',
  callReturnAction: 'Вернуться',
  callCameraUnavailable: 'Не удалось включить камеру.',
  callMinimize: 'Свернуть звонок',
  callReturnBar: 'Вернуться к звонку',
  callReturnBarHint: 'Открывает экран звонка',
  callPhaseCalling: 'Вызов…',
  callPhaseRinging: 'Звонит…',
  callEndBusy: 'Занято',
  callEndDeclined: 'Вызов отклонён',
  callEndNoAnswer: 'Нет ответа',
  callEndFailed: 'Звонок прервался',
  callEndEnded: 'Звонок завершён',
  callEndCallAgain: 'Позвонить снова',
  callEndMessage: 'Написать',
  callCameraStop: 'Выкл. видео',
  callCameraStart: 'Вкл. видео',
  callSwitchToVideo: 'Видео',
  callCameraOffLabel: 'Камера выключена',
  callMicOffLabel: 'Микрофон выключен',
  callWeakNetwork: 'Слабая связь',
  callTileFocusHint: 'Нажмите, чтобы увеличить',
  callTileUnfocusHint: 'Нажмите, чтобы показать всех',
  callSelfViewHint: 'Перетащите, чтобы переместить. Нажмите, чтобы поменять местами с видео собеседника.',
  callGroupConfirmTitle: 'Позвонить {count, plural, one {# участнику} other {# участникам}}?',
  callGroupConfirmBody: 'Звонок получат все участники «{title}».',
  callGroupConfirmAction: 'Позвонить',
};
const ruHistory: Group<typeof enHistory> = {
  callHistOutgoing: 'Исходящий звонок',
  callHistOutgoingVideo: 'Исходящий видеозвонок',
  callHistIncoming: 'Входящий звонок',
  callHistIncomingVideo: 'Входящий видеозвонок',
  callHistGroup: 'Групповой звонок',
  callHistGroupVideo: 'Групповой видеозвонок',
  callHistMissed: 'Пропущенный звонок',
  callHistMissedVideo: 'Пропущенный видеозвонок',
  callHistCancelled: 'Отменённый звонок',
  callHistNoAnswer: 'Нет ответа',
  callHistDeclinedOutgoing: 'Вызов отклонён',
  callHistDeclinedIncoming: 'Отклонённый звонок',
  callHistBusy: 'Занято',
  callHistEnded: 'Разговор завершён',
  callHistCallBackHint: 'Нажмите, чтобы перезвонить',
};
const ruNotify: Group<typeof enNotify> = {
  callNotifyAnswer: 'Ответить',
  callNotifyDecline: 'Отклонить',
  callNotifyCallBack: 'Перезвонить',
  callNotifyMessage: 'Написать',
  callNotifyMissedTitle: 'Пропущенный звонок',
  callNotifyMissedVideoTitle: 'Пропущенный видеозвонок',
  callNotifyMissedGroupTitle: 'Пропущенный групповой звонок',
  callNotifyMissedFrom: 'от {name}',
  callNotifySilencedBody: 'Звук выключен. Нажмите, чтобы открыть звонок',
  callAnswerWithoutVideo: 'Ответить без видео',
  callReplyWithMessage: 'Ответить сообщением',
  callQuickReplyCantTalk: 'Не могу говорить. Перезвоню позже.',
  callQuickReplyFewMinutes: 'Перезвоню через несколько минут.',
  callQuickReplyTextMe: 'Напишите, пожалуйста.',
  callKeepPermissionTitle: 'Разрешения',
  callKeepPermissionBody: 'Нужен доступ к телефонии для входящих звонков.',
  callKeepPermissionAllow: 'Разрешить',
};

const srScreen: Group<typeof enScreen> = {
  callAlreadyInCall: 'Već ste u pozivu',
  callReturnAction: 'Vrati se',
  callCameraUnavailable: 'Kamera nije mogla da se uključi.',
  callMinimize: 'Umanji poziv',
  callReturnBar: 'Vrati se u poziv',
  callReturnBarHint: 'Otvara ekran poziva',
  callPhaseCalling: 'Pozivanje…',
  callPhaseRinging: 'Zvoni…',
  callEndBusy: 'Zauzeto',
  callEndDeclined: 'Poziv odbijen',
  callEndNoAnswer: 'Bez odgovora',
  callEndFailed: 'Poziv je prekinut',
  callEndEnded: 'Poziv završen',
  callEndCallAgain: 'Pozovi ponovo',
  callEndMessage: 'Piši',
  callCameraStop: 'Isključi video',
  callCameraStart: 'Uključi video',
  callSwitchToVideo: 'Na video',
  callCameraOffLabel: 'Kamera je isključena',
  callMicOffLabel: 'Mikrofon je isključen',
  callWeakNetwork: 'Slaba veza',
  callTileFocusHint: 'Dodirnite da uvećate',
  callTileUnfocusHint: 'Dodirnite da prikažete sve',
  callSelfViewHint: 'Prevucite da pomerite. Dodirnite da zamenite mesta sa drugim videom.',
  callGroupConfirmTitle: 'Pozvati {count, plural, one {# člana} few {# člana} other {# članova}}?',
  callGroupConfirmBody: 'Poziv će dobiti svi u grupi „{title}“.',
  callGroupConfirmAction: 'Pozovi',
};
const srHistory: Group<typeof enHistory> = {
  callHistOutgoing: 'Odlazni poziv',
  callHistOutgoingVideo: 'Odlazni video-poziv',
  callHistIncoming: 'Dolazni poziv',
  callHistIncomingVideo: 'Dolazni video-poziv',
  callHistGroup: 'Grupni poziv',
  callHistGroupVideo: 'Grupni video-poziv',
  callHistMissed: 'Propušten poziv',
  callHistMissedVideo: 'Propušten video-poziv',
  callHistCancelled: 'Otkazan poziv',
  callHistNoAnswer: 'Bez odgovora',
  callHistDeclinedOutgoing: 'Poziv odbijen',
  callHistDeclinedIncoming: 'Odbijen poziv',
  callHistBusy: 'Zauzeto',
  callHistEnded: 'Poziv završen',
  callHistCallBackHint: 'Dodirnite da uzvratite poziv',
};
const srNotify: Group<typeof enNotify> = {
  callNotifyAnswer: 'Odgovori',
  callNotifyDecline: 'Odbij',
  callNotifyCallBack: 'Uzvrati poziv',
  callNotifyMessage: 'Piši',
  callNotifyMissedTitle: 'Propušten poziv',
  callNotifyMissedVideoTitle: 'Propušten video-poziv',
  callNotifyMissedGroupTitle: 'Propušten grupni poziv',
  callNotifyMissedFrom: 'od {name}',
  callNotifySilencedBody: 'Zvuk je isključen. Dodirnite da otvorite poziv',
  callAnswerWithoutVideo: 'Odgovori bez videa',
  callReplyWithMessage: 'Odgovori porukom',
  callQuickReplyCantTalk: 'Ne mogu sada da pričam. Zvaću vas kasnije.',
  callQuickReplyFewMinutes: 'Zvaću vas za nekoliko minuta.',
  callQuickReplyTextMe: 'Molim vas, pišite mi.',
  callKeepPermissionTitle: 'Dozvole',
  callKeepPermissionBody: 'Za dolazne pozive potreban je pristup telefoniji.',
  callKeepPermissionAllow: 'Dozvoli',
};

const frScreen: Group<typeof enScreen> = {
  callAlreadyInCall: 'Vous êtes déjà en appel',
  callReturnAction: 'Revenir',
  callCameraUnavailable: "Impossible d'activer la caméra.",
  callMinimize: "Réduire l'appel",
  callReturnBar: "Revenir à l'appel",
  callReturnBarHint: "Ouvre l'écran d'appel",
  callPhaseCalling: 'Appel…',
  callPhaseRinging: 'Ça sonne…',
  callEndBusy: 'Occupé',
  callEndDeclined: 'Appel refusé',
  callEndNoAnswer: 'Pas de réponse',
  callEndFailed: 'Appel interrompu',
  callEndEnded: 'Appel terminé',
  callEndCallAgain: 'Rappeler',
  callEndMessage: 'Écrire',
  callCameraStop: 'Couper la vidéo',
  callCameraStart: 'Activer la vidéo',
  callSwitchToVideo: 'Vidéo',
  callCameraOffLabel: 'Caméra coupée',
  callMicOffLabel: 'Micro coupé',
  callWeakNetwork: 'Connexion faible',
  callTileFocusHint: 'Touchez pour agrandir',
  callTileUnfocusHint: 'Touchez pour voir tout le monde',
  callSelfViewHint: "Faites glisser pour déplacer. Touchez pour échanger avec l'autre vidéo.",
  callGroupConfirmTitle: 'Appeler {count, plural, one {# membre} other {# membres}} ?',
  callGroupConfirmBody: 'Tous les membres de « {title} » recevront l’appel.',
  callGroupConfirmAction: 'Appeler',
};
const frHistory: Group<typeof enHistory> = {
  callHistOutgoing: 'Appel sortant',
  callHistOutgoingVideo: 'Appel vidéo sortant',
  callHistIncoming: 'Appel entrant',
  callHistIncomingVideo: 'Appel vidéo entrant',
  callHistGroup: 'Appel de groupe',
  callHistGroupVideo: 'Appel vidéo de groupe',
  callHistMissed: 'Appel manqué',
  callHistMissedVideo: 'Appel vidéo manqué',
  callHistCancelled: 'Appel annulé',
  callHistNoAnswer: 'Pas de réponse',
  callHistDeclinedOutgoing: 'Refusé',
  callHistDeclinedIncoming: 'Appel refusé',
  callHistBusy: 'Occupé',
  callHistEnded: 'Appel terminé',
  callHistCallBackHint: 'Touchez pour rappeler',
};
const frNotify: Group<typeof enNotify> = {
  callNotifyAnswer: 'Répondre',
  callNotifyDecline: 'Refuser',
  callNotifyCallBack: 'Rappeler',
  callNotifyMessage: 'Écrire',
  callNotifyMissedTitle: 'Appel manqué',
  callNotifyMissedVideoTitle: 'Appel vidéo manqué',
  callNotifyMissedGroupTitle: 'Appel de groupe manqué',
  callNotifyMissedFrom: 'de {name}',
  callNotifySilencedBody: "Sonnerie coupée. Touchez pour ouvrir l'appel",
  callAnswerWithoutVideo: 'Répondre sans vidéo',
  callReplyWithMessage: 'Répondre par message',
  callQuickReplyCantTalk: 'Je ne peux pas parler. Je vous rappelle plus tard.',
  callQuickReplyFewMinutes: 'Je vous rappelle dans quelques minutes.',
  callQuickReplyTextMe: 'Écrivez-moi, s’il vous plaît.',
  callKeepPermissionTitle: 'Autorisations',
  callKeepPermissionBody: 'L’accès à la téléphonie est nécessaire pour recevoir les appels.',
  callKeepPermissionAllow: 'Autoriser',
};

export const callsMessages: Record<AppLocale, Record<CallsMessageKey, string>> = {
  en,
  ru: { ...ruScreen, ...ruHistory, ...ruNotify },
  sr: { ...srScreen, ...srHistory, ...srNotify },
  fr: { ...frScreen, ...frHistory, ...frNotify },
};
